class RecoveryOffer extends HTMLElement {
  #abortController = new AbortController();
  #delayTimer;
  #eligible = false;
  #shown = false;
  #storageKey = 'regalos-express-recovery-offer-v1';

  connectedCallback() {
    this.dialog = this.querySelector('[data-recovery-dialog]');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    const { signal } = this.#abortController;
    const isDesignMode = this.dataset.designMode === 'true';
    const discountApplied = this.dataset.discountApplied === 'true';
    const previousDecision = this.#readDecision();

    this.querySelectorAll('[data-recovery-dismiss]').forEach((button) => {
      button.addEventListener('click', this.#dismiss, { signal });
    });

    this.querySelector('[data-recovery-accept]')?.addEventListener('click', this.#accept, { signal });
    this.dialog.addEventListener('cancel', this.#dismiss, { signal });
    this.dialog.addEventListener('click', this.#handleBackdropClick, { signal });
    this.dialog.addEventListener('close', this.#handleClose, { signal });

    if (isDesignMode) {
      this.#eligible = true;
      this.#show();
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
      return;
    }

    if (discountApplied || previousDecision) return;

    const delaySeconds = Math.max(Number(this.dataset.delay) || 25, 10);
    this.#delayTimer = window.setTimeout(() => {
      this.#eligible = true;
    }, delaySeconds * 1000);

    document.addEventListener('mouseout', this.#handleDesktopExit, { signal });
    window.addEventListener('scroll', this.#handleMobileScroll, { signal, passive: true });
  }

  disconnectedCallback() {
    window.clearTimeout(this.#delayTimer);
    this.#abortController.abort();
    if (this.dialog?.open) this.dialog.close();
  }

  #handleDesktopExit = (event) => {
    if (!this.#eligible || event.relatedTarget || event.clientY > 12) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    this.#show();
  };

  #handleMobileScroll = () => {
    if (!this.#eligible || window.matchMedia('(pointer: fine)').matches) return;

    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0 || window.scrollY / scrollable < 0.6) return;
    this.#show();
  };

  #handleBackdropClick = (event) => {
    if (event.target !== this.dialog) return;
    this.#dismiss();
  };

  #handleClose = () => {
    if (this.#shown && !this.#readDecision()) this.#writeDecision('dismissed');
  };

  #handleSectionSelect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.#show();
  };

  #show() {
    if (this.#shown || this.dialog.open) return;
    if (document.querySelector('dialog[open]')) return;

    this.#shown = true;
    this.dialog.showModal();
  }

  #dismiss = (event) => {
    event?.preventDefault();
    this.#writeDecision('dismissed');
    this.dialog.close();
  };

  #accept = () => {
    this.#writeDecision('accepted');
  };

  #readDecision() {
    try {
      return window.sessionStorage.getItem(this.#storageKey);
    } catch (_error) {
      return null;
    }
  }

  #writeDecision(value) {
    try {
      window.sessionStorage.setItem(this.#storageKey, value);
    } catch (_error) {
      // The offer still works when storage is unavailable.
    }
  }
}

if (!customElements.get('re-recovery-offer')) {
  customElements.define('re-recovery-offer', RecoveryOffer);
}
